// RelistNeedDialog — #281. Relist an EXPIRED posting: it must get a NEW future
// deadline (else it re-expires on the next sweep and the apply-guard keeps
// refusing applicants). Sets the deadline (updateNeed) then flips expired→active
// (the guarded funnel). Shared by the Rekrytering list ⋮ and the detail page so
// the relist UX is identical in both places.
import React, { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/custom-ui';
import { useToast } from '@/hooks/use-toast';
import { needApi } from '@/lib/api/needApi';
import type { Need } from '@/lib/models/need';

const isoInDays = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

interface RelistNeedDialogProps {
    need: Need | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onDone?: () => void;
}

export const RelistNeedDialog: React.FC<RelistNeedDialogProps> = ({ need, open, onOpenChange, onDone }) => {
    const { toast } = useToast();
    const [date, setDate] = useState('');
    const [busy, setBusy] = useState(false);
    const minDate = isoInDays(1);

    useEffect(() => { if (open) setDate(isoInDays(30)); }, [open]);

    const submit = async () => {
        if (!need || !date || date < minDate) {
            toast({ title: 'Välj ett datum i framtiden', variant: 'destructive' });
            return;
        }
        setBusy(true);
        try {
            // Dedicated relist endpoint: sets the new deadline + flips expired→active
            // atomically (the general PUT would zero the work-location booleans).
            await needApi.relistNeed(need.id, date);
            onOpenChange(false);
            toast({ title: 'Annonsen är återpublicerad', description: `Ny sista ansökningsdag: ${date}` });
            onDone?.();
        } catch {
            toast({ title: 'Kunde inte återpublicera annonsen', variant: 'destructive' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <ConfirmDialog
            open={open}
            onOpenChange={(o) => { if (!o) onOpenChange(false); }}
            title="Förläng & återpublicera"
            description={
                <div className="space-y-2">
                    <p>Annonsen får en ny sista ansökningsdag och blir synlig för kandidater igen.</p>
                    <label className="block text-sm font-medium text-gray-700">
                        Ny sista ansökningsdag
                        <input
                            type="date"
                            value={date}
                            min={minDate}
                            onChange={(e) => setDate(e.target.value)}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#1A4D4F] focus:ring-[#1A4D4F]"
                        />
                    </label>
                </div>
            }
            confirmLabel="Återpublicera"
            isBusy={busy}
            onConfirm={submit}
        />
    );
};

export default RelistNeedDialog;
